import { useState, useEffect, useCallback, useRef } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSidePanel } from '../features/sidePanelSlice';
import { setChannel, setOnboardingTourSteps, setOnboardingDefaultTourPending } from '../features/channelSlice';

// Suppress React Joyride's borderRadius warning on SVG elements
// This is a known issue with the library passing invalid props to SVG
const originalConsoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('borderRadius') && args[0].includes('DOM element')) {
    return; // Suppress this specific warning
  }
  originalConsoleError.apply(console, args);
};

// Local storage keys
const TOUR_COMPLETED_KEY = 'guidedTourCompleted';
const TOUR_ENABLED_KEY = 'guidedTourEnabled';
const TOUR_SHOWN_KEY = 'guidedTourShown'; // Track if tour has been shown at least once

// Step IDs for easier management
const STEP_IDS = {
  // Welcome
  WELCOME: 'welcome',
  
  // Overview steps
  DEVICE: 'device',
  STATUS_BAR: 'status-bar',
  HOME_BUTTON: 'home-button',
  CHANNELS: 'channels',
  
  // Mobile footer buttons
  FOOTER_CUSTOMER: 'footer-customer',
  FOOTER_EMPLOYEE: 'footer-employee',
  
  // Individual channel steps
  CHANNEL_PHONE: 'channel-phone',
  CHANNEL_SMS: 'channel-sms',
  CHANNEL_EMAIL: 'channel-email',
  CHANNEL_CALENDAR: 'channel-calendar',
  CHANNEL_BROWSER: 'channel-browser',
  CHANNEL_AGENT: 'channel-agent',
  CHANNEL_DXA: 'channel-dxa',
  
  // Action button steps
  ACTION_RECAP: 'action-recap',
  ACTION_SETTINGS: 'action-settings',
  ACTION_BRAND: 'action-brand',
  ACTION_STORY: 'action-story',
  ACTION_LANGUAGE: 'action-language',
  ACTION_DEVICE: 'action-device',
  ACTION_THEME: 'action-theme',
  ACTION_TOUR: 'action-tour',
  ACTION_HELP: 'action-help',
  ACTION_RESET: 'action-reset',
  ACTION_QUIT: 'action-quit',
  
  // Side panel
  SIDEPANEL: 'sidepanel',
  
  // Final step - Get Started
  GET_STARTED: 'get-started',
};

/**
 * ============================================================================
 * HOW TO MODIFY THE GUIDED TOUR STEPS
 * ============================================================================
 * 
 * To add, remove, or modify tour steps, edit the `getTourSteps` function below.
 * 
 * STEP STRUCTURE:
 * Each step is an object with the following properties:
 * 
 *   {
 *     id: 'unique-step-id',           // Unique identifier (add to STEP_IDS above)
 *     target: '.css-selector',         // CSS selector for the element to highlight
 *     content: <JSX>,                  // The content/text shown in the tooltip
 *     placement: 'right',              // Tooltip position: 'top', 'bottom', 'left', 'right', 'center'
 *     disableBeacon: true,             // Whether to hide the pulsing beacon (usually true)
 *     spotlightClicks: false,          // Whether to allow clicks on the highlighted element
 *     spotlightPadding: 10,            // Extra padding around the spotlight
 *   }
 * 
 * TO ADD A NEW STEP:
 *   1. Add a new ID to STEP_IDS constant above
 *   2. Add the step object to the `steps` array in getTourSteps()
 *   3. Make sure the target CSS selector exists in your UI
 * 
 * TO REMOVE A STEP:
 *   1. Simply remove the step object from the `steps` array
 * 
 * TO MODIFY A STEP:
 *   1. Find the step in the array and edit its properties
 * 
 * DYNAMIC CONTENT:
 *   The function receives `deviceType` and `sidePanelExpanded` params,
 *   so you can customize step content based on UI state.
 * 
 * ============================================================================
 * TARGETING INDIVIDUAL CHANNELS & FOOTER BUTTONS
 * ============================================================================
 * 
 * Each channel tile on the home screen has a `data-channel` attribute with
 * the channel key (e.g., "phone", "sms", "email", "calendar", etc.)
 * 
 * You can target specific channels using CSS attribute selectors:
 *   - Phone:    '[data-channel="phone"]'
 *   - SMS:      '[data-channel="sms"]'
 *   - Email:    '[data-channel="email"]'
 *   - Calendar: '[data-channel="calendar"]'
 *   - Browser:  '[data-channel="browser"]'
 *   - Agent:    '[data-channel="agent"]'
 *   - Any channel with class: '.channel-tile'
 * 
 * Footer buttons (mobile only) have `data-tour-target` attributes:
 *   - Customer Button: '[data-tour-target="footer-customer"]'
 *   - Employee Button: '[data-tour-target="footer-employee"]'
 * 
 * NOTE: Channels are conditionally loaded based on config, so steps
 * targeting specific channels will automatically be skipped if the
 * channel isn't visible. The tour filters out steps with missing targets.
 * The mobile/desktop logic automatically shows appropriate navigation
 * steps based on screen size.
 * 
 * EXAMPLE - Adding a step for the Phone channel:
 * 
 *   // Add to STEP_IDS:
 *   const STEP_IDS = {
 *     ...existing ids,
 *     PHONE_CHANNEL: 'phone-channel',
 *   };
 * 
 *   // Add to steps array in getTourSteps():
 *   {
 *     id: STEP_IDS.PHONE_CHANNEL,
 *     target: '[data-channel="phone"]',
 *     content: (
 *       <div>
 *         <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>� Phone</h3>
 *         <p style={{ margin: 0, lineHeight: 1.5 }}>
 *           Make and receive phone calls. Experience voice interactions with AI agents.
 *         </p>
 *       </div>
 *     ),
 *     placement: 'bottom',
 *     disableBeacon: true,
 *     spotlightClicks: true,  // Allow user to click and open the channel
 *     spotlightPadding: 5,
 *   },
 * 
 * ============================================================================
 */

// Get tour steps dynamically based on current UI state
const getTourSteps = (deviceType, sidePanelExpanded) => {
  // Check if we're in mobile/tablet view (footer carousel visible)
  // Use both window size and DOM presence for robust detection
  const isMobileView = typeof window !== 'undefined' && (
    window.innerWidth < 1024 || 
    document.querySelector('.footer-carousel') !== null
  );
  
  console.log(`🎯 Generating tour steps - isMobileView: ${isMobileView}, window width: ${window?.innerWidth}, footer carousel: ${!!document.querySelector('.footer-carousel')}`);
  
  const steps = [
    // ==================== WELCOME (ALWAYS SHOWN) ====================
    {
      id: STEP_IDS.WELCOME,
      target: isMobileView ? 'body' : '.device-frame-container',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Welcome to Ask Cara</h3>
          <p style={{ margin: 0, lineHeight: 1.5}}>
            This tour will walk you through the key features of the Ask Cara platform.
            Once completed, you'll be able to simulate customer interactions across multiple channels, exploring how agentic workflows can enhance customer engagement and support
            for your business using our unique collection of tools, accelerators, expertise and the power of Pega Infinity.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      spotlightClicks: false,
      spotlightPadding: 0,
    },
    
    // ==================== DEVICE OVERVIEW ====================
    {
      id: STEP_IDS.DEVICE,
      target: isMobileView ? 'body' : '.device-frame-container',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Your Virtual Device</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            This is a simulated {deviceType === 'tablet' ? 'tablet' : 'phone'} where you can experience customer interactions. 
            It shows notifications, messages, apps, and phone calls just like a real device.
          </p>
        </div>
      ),
      placement: isMobileView ? 'center' : 'right',
      disableBeacon: true,
      spotlightClicks: false,
      spotlightPadding: 10,
    },
    
    // ==================== NAVIGATION BUTTONS (CONDITIONAL) ====================
    // Mobile: Footer Customer/Employee buttons
    ...(isMobileView ? [
      {
        id: STEP_IDS.FOOTER_CUSTOMER,
        target: '[data-tour-target="footer-customer"]', // Customer/Home button
        content: (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Customer View</h3>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              Tap here to return to the customer's perspective and access the home screen with all available communication channels.
              This works just like a home button on a real device.
            </p>
          </div>
        ),
        placement: 'top',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 8,
      },
      {
        id: STEP_IDS.FOOTER_EMPLOYEE,
        target: '[data-tour-target="footer-employee"]', // Employee button
        content: (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Employee View</h3>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              Switch to the employee perspective to see tools, customer information, and real-time updates that support staff use during customer interactions.
            </p>
          </div>
        ),
        placement: 'top',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 8,
      }
    ] : [
      // Desktop: Home button
      {
        id: STEP_IDS.HOME_BUTTON,
        target: '.home-button',
        content: (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Home Button</h3>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              Use the Home button to return to the main screen at any time and switch apps.
              It works just like a real device's home button.
            </p>
          </div>
        ),
        placement: 'top',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 8,
      }
    ]),
    
    // ==================== CHANNELS OVERVIEW ====================
    {
      id: STEP_IDS.CHANNELS,
      target: '.home-screen-tiles',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Home Screen</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            These app icons represent different ways your customers can interact with you and your business.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: false,
      spotlightPadding: 15,
    },
    
    // ==================== INDIVIDUAL CHANNELS ====================
    {
      id: STEP_IDS.CHANNEL_PHONE,
      target: '[data-channel="call"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Phone</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Simulates phone conversations with incoming and outgoing calls, 
            allows you to speak with AI agents using voice interactions.
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_SMS,
      target: '[data-channel="sms"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Messages (SMS)</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            View text message conversations between your business and customers. 
            See realistic notifications, conversation history, and automated responses in action.
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_EMAIL,
      target: '[data-channel="email"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Email</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Browse a full email inbox with organized conversations, 
            See realistic notifications, conversation history, and automated responses in action.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_CALENDAR,
      target: '[data-channel="calendar"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Calendar</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Visualize appointment scheduling, available time slots, 
            and how customers book meetings or services with your business.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_BROWSER,
      target: '[data-channel="browser"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Browser</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            An embedded web experience showing how customers browse your public website 
            and how web interactions integrate with your business.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_AGENT,
      target: '[data-channel="agent"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Chat</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            AI-powered customer service chat with real-time conversations, 
            voice interaction capabilities, and intelligent automated responses.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    {
      id: STEP_IDS.CHANNEL_DXA,
      target: '[data-channel="app"]',  // The DXA/Self-Service channel uses key "app" in config
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Self Service App</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Digital self-service portal powered by Pega DX API and Labbds DX Accelerator
            Customers can manage their accounts, submit requests, and complete tasks independently.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 5,
    },
    
    // ==================== SIDE PANEL (DESKTOP ONLY) ====================
    ...(isMobileView ? [] : [{
      id: STEP_IDS.SIDEPANEL,
      target: sidePanelExpanded ? '.employee-pane-fixed' : '[title="Expand Side Panel"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Employee View</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            {sidePanelExpanded 
              ? "This panel shows what your support staff sees - tools, customer information, and real-time updates as customers interact."
              : "Click to expand the Employee Side Panel and see the agent's perspective during customer interactions."
            }
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: sidePanelExpanded ? 20 : 5,
    }]),
    
    // ==================== ACTION BUTTONS ====================
    {
      id: STEP_IDS.ACTION_SETTINGS,
      target: '[title="Settings"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Settings</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Configure audio devices, vision devices, switch between phone and tablet views, 
            and customize your demo experience.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_STORY,
      target: '[title="Story"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Stories</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Switch between different demonstration 'stories'
            Select a story that aligns with your industry.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_BRAND,
      target: '[title="Brand"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Brands</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Hot-swap between different company brands. Each brand has custom colors, 
            logos, and unique settings. Showcases the power of DXA and the DX API.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_LANGUAGE,
      target: '[title="Language"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Agent Language</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Choose the language the AI agent speaks during voice and video calls.
            Pick from the languages supported by the live model.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_DEVICE,
      target: '[data-action^="device"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Device</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Switch the simulated device between phone and tablet to preview how
            the experience adapts to each.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_THEME,
      target: '[data-action$="-mode"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Light &amp; Dark Mode</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Toggle the Cara interface between light and dark appearance to suit
            the room or your preference.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_RECAP,
      target: '[title="Recap"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Recap</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            View a history of recent events that have been triggered as you work through real service cases, 
            visualise API calls, engineers being dispatched and post being delivered.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_TOUR,
      target: '[title="Tour"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Tour</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Click here at any time to restart this guided tour and re-explore the platform features.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_HELP,
      target: '[title="Help"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Help</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Access the full documentation, contact support,
            or restart this guided tour anytime.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_RESET,
      target: '[title="Reset"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Reset</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Clears all notifications, messages, and returns the demo 
            to its initial state. Perfect for starting a new demonstration.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    {
      id: STEP_IDS.ACTION_QUIT,
      target: '[title="Quit"]',
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Quit</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            When you're finished you can end your session and securely sign out of the platform. 
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 8,
    },
    
    // ==================== GET STARTED - FINAL STEP ====================
    {
      id: STEP_IDS.GET_STARTED,
      target: 'body',  // Always visible, centered on screen
      content: (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>You're all set!</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Open the Phone app from the home screen and tap on a contact to make a call.
            See how Cara can help you today with AI-powered customer service.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      spotlightClicks: false,
      spotlightPadding: 0,
    },
  ];

  return steps;
};

// Custom Tooltip component
const CustomTooltip = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  return (
    <div
      {...tooltipProps}
      style={{
        background: 'var(--cara-surface)',
        color: 'var(--cara-text)',
        borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(68, 194, 253, 0.3)',
        padding: '24px',
        maxWidth: '380px',
        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ 
          fontSize: '11px', 
          color: '#44C2FD', 
          fontWeight: '600', 
          textTransform: 'uppercase', 
          letterSpacing: '1.5px' 
        }}>
          Step {index + 1} of {size}
        </span>
        <button 
          {...closeProps}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.target.style.color = '#fff'; e.target.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.target.style.color = '#6b7280'; e.target.style.background = 'transparent'; }}
        >
          <span className="material-icons" style={{ fontSize: '20px' }}>close</span>
        </button>
      </div>
      
      {/* Content */}
      <div style={{ color: 'var(--cara-text)', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
        {step.content}
      </div>
      
      {/* Progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
        {Array.from({ length: size }).map((_, i) => (
          <div
            key={i}
            style={{
              width: i === index ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: i === index ? '#44C2FD' : i < index ? 'rgba(68, 194, 253, 0.5)' : '#374151',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
      
      {/* Navigation buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Left: Skip Tour */}
        <button
          onClick={() => {
            const skipEvent = new CustomEvent('joyride:skip');
            document.dispatchEvent(skipEvent);
          }}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            background: 'transparent',
            color: 'var(--cara-text-muted)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          Skip Tour
        </button>

        {/* Right: Back + Next grouped together */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            {...backProps}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              border: '2px solid #44C2FD',
              background: 'transparent',
              color: '#44C2FD',
              cursor: 'pointer',
              transition: 'all 0.2s',
              visibility: index === 0 ? 'hidden' : 'visible',
            }}
          >
            Back
          </button>

          <button
            {...primaryProps}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              background: 'linear-gradient(135deg, #44C2FD 0%, #23A6D5 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(68, 194, 253, 0.4)',
              transition: 'all 0.2s',
            }}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Clamp a position so a box of (width, height) stays inside the viewport with margin.
const clampToViewport = (left, top, width, height, margin = 16) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    left: Math.max(margin, Math.min(left, vw - width - margin)),
    top: Math.max(margin, Math.min(top, vh - height - margin)),
  };
};

// ── Onboarding tour / nudge stacking band ───────────────────────────────────
// These MUST sit above the device area (.main-content establishes a stacking
// context at z-index:10, so the whole device + bezel lives at root-level 10) but
// BELOW the employee side panel (z-index:50) and BELOW all modals (z-index:9999+).
// That way tours/nudges always cover the device but are never drawn over a modal
// or the side panel. Keep every value < 50.
const TOUR_Z_BASE = 44;    // dim overlay / spotlight / pulsing ring
const TOUR_Z_ACCENT = 45;  // beacon / pointer
const TOUR_Z_TOOLTIP = 46; // tooltip / caption (top of the band, still < 50)

// Custom overlay for config-driven onboarding tour steps.
// Supports two modes per step:
//   mode: 'modal' (default) — full spotlight + dim overlay + dialog with Back/Next.
//   mode: 'nudge'           — animated pointer + caption near target, no overlay,
//                             no dialog; auto-dismisses when the target is clicked.
const OnboardingTourOverlay = ({ steps, onComplete }) => {
  const dispatch = useDispatch();
  const currentChannel = useSelector((state) => state.channel.currentChannel);
  const isCallInProgress = useSelector((state) => state.channel.isCallInProgress);
  const [index, setIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = steps[index];
  const pad = step?.spotlightPadding ?? 10;
  const placement = step?.placement || 'bottom';
  const mode = step?.mode || 'modal';
  const allowClickThrough = mode === 'nudge' || step?.spotlightClicks === true;

  // Derive the channel the nudge is pointing at (if any), e.g. target
  // `[data-channel="call"]` → 'call'. Allow that channel through the lock so
  // the user can complete the intended action without being bounced back.
  const nudgeTargetChannel = (() => {
    if (mode !== 'nudge' || !step?.target) return null;
    const m = String(step.target).match(/data-channel=["']?([^"'\]]+)/);
    return m ? m[1] : null;
  })();

  // True when this nudge points at a call-initiating action (the voice/video
  // icons carry data-action="voice"|"video"). Used as a belt-and-braces dismiss:
  // if the tap that starts the call is missed by the click/pointerup listener
  // (flaky on touch), the call actually starting still advances the nudge.
  const nudgeStartsCall =
    mode === 'nudge' && /data-action=["']?(voice|video)/.test(String(step?.target || ''));

  // Lock navigation back to home, but ONLY for nudges that actually point at a
  // home-screen channel tile (selector contains `data-channel=...`). For
  // arbitrary in-channel or SidePanel selectors (e.g. the camera icon inside
  // the phone channel) the nudge needs to render wherever the user currently
  // is — bouncing them home would tear the target out from under the nudge.
  useEffect(() => {
    if (mode !== 'nudge') return;
    if (!nudgeTargetChannel) return;
    if (!currentChannel || currentChannel === 'home') return;
    if (currentChannel === nudgeTargetChannel) return;
    dispatch(setChannel('home'));
  }, [mode, currentChannel, nudgeTargetChannel, dispatch]);

  const advance = useCallback(() => {
    if (index < steps.length - 1) {
      setTargetRect(null);
      setIndex(i => i + 1);
    } else {
      onComplete();
    }
  }, [index, steps.length, onComplete]);

  // Measure target element on mount/resize/scroll
  useEffect(() => {
    if (!step?.target) return;

    let rafId = null;
    let lastRect = null;
    const rectsEqual = (a, b) =>
      a && b &&
      a.top === b.top && a.left === b.left &&
      a.width === b.width && a.height === b.height;

    // rAF-driven measurement: catches not only resize/scroll but also ancestor
    // layout shifts that don't fire window events — e.g. the side panel sliding
    // open/closed and pushing the device bezel across the viewport. Cheap to
    // run; the overlay is short-lived.
    const tick = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        if (!rectsEqual(lastRect, r)) {
          lastRect = r;
          setTargetRect(r);
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [step]);

  // If the step lets the user click the target (nudge or spotlightClicks),
  // dismiss/advance when the user taps the target.
  //
  // We use DELEGATED, capture-phase listeners on `document` rather than binding
  // to the matched element directly, because direct binding is unreliable here:
  //  - The target can mount AFTER this step activates (e.g. the camera icon
  //    [data-action="video"] only renders once we're in the call channel), and
  //    React can REPLACE the node on later re-renders (the lucide <Video> SVG),
  //    leaving a one-shot listener stranded on a detached element.
  //  - On touch, a synthesized `click` doesn't always fire (a slight finger
  //    move is treated as a scroll/swipe), so the tap never dismisses the nudge.
  //
  // Delegating on document sidesteps node replacement (the document is always
  // there); capture phase means we still fire even if the target's own handler
  // calls stopPropagation; and listening to `pointerup` as well as `click`
  // covers touch taps that never produce a click. A `fired` latch keeps a single
  // tap (which emits both pointerup AND click) from advancing twice. We never
  // preventDefault/stopPropagation, so the target's real action (e.g. starting
  // the video call) still runs alongside the dismiss.
  useEffect(() => {
    if (!step?.target || !allowClickThrough) return;
    let fired = false;
    const handler = (e) => {
      if (fired) return;
      const hit = e.target?.closest?.(step.target);
      if (hit) {
        fired = true;
        advance();
      }
    };
    document.addEventListener('pointerup', handler, true);
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('pointerup', handler, true);
      document.removeEventListener('click', handler, true);
    };
  }, [step, allowClickThrough, advance]);

  // Belt-and-braces for nudge: if the user reaches the nudge's target channel
  // by any path (animated tile-open, deep link, etc.), advance the step. This
  // catches the case where the channel change is delayed by an animation and
  // the native click listener was missed or torn down before firing.
  useEffect(() => {
    if (mode !== 'nudge' || !nudgeTargetChannel) return;
    if (currentChannel === nudgeTargetChannel) {
      advance();
    }
  }, [mode, nudgeTargetChannel, currentChannel, advance]);

  // Belt-and-braces for call-action nudges (voice/video icons): if the user
  // taps the icon and the call starts but the tap was missed by the click/
  // pointerup listener (touch can swallow these), advancing here on the actual
  // call-in-progress signal still dismisses the nudge.
  useEffect(() => {
    if (!nudgeStartsCall) return;
    if (isCallInProgress) {
      advance();
    }
  }, [nudgeStartsCall, isCallInProgress, advance]);

  const handleBack = () => {
    setTargetRect(null);
    setIndex(i => i - 1);
  };

  if (!step || !targetRect) return null;

  const targetCx = targetRect.left + targetRect.width / 2;
  const targetCy = targetRect.top + targetRect.height / 2;

  // ─────────────────────────── Nudge mode ───────────────────────────
  // Lightweight pointer + caption. No overlay, no spotlight, no buttons.
  if (mode === 'nudge') {
    // Defence-in-depth: only blank the nudge when it points at a home-screen
    // channel tile AND we're somewhere else. Nudges with non-channel selectors
    // (in-channel icons, SidePanel elements) don't care about currentChannel
    // and must keep rendering against their actual target.
    if (nudgeTargetChannel && currentChannel !== 'home' && currentChannel !== nudgeTargetChannel) return null;
    const captionWidth = 220;
    const captionHeight = 56;
    // Default caption position: below the target. If no space below, flip above.
    const below = window.innerHeight - (targetRect.bottom + 80) > captionHeight;
    const rawTop = below ? targetRect.bottom + 56 : targetRect.top - captionHeight - 56;
    const rawLeft = targetCx - captionWidth / 2;
    const clamped = clampToViewport(rawLeft, rawTop, captionWidth, captionHeight);

    // Pointer hand floats just above (or below) the target, pointing at it.
    const pointerSize = 36;
    const pointerTop = below
      ? targetRect.bottom + 8
      : targetRect.top - pointerSize - 8;
    const pointerLeft = targetCx - pointerSize / 2;

    return (
      <>
        <style>{`
          @keyframes onboarding-nudge-bounce {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(${below ? '8px' : '-8px'}); }
          }
          @keyframes onboarding-nudge-pulse {
            0%, 100% { transform: scale(1); opacity: 0.7; }
            50%      { transform: scale(1.25); opacity: 0; }
          }
          @keyframes onboarding-nudge-fade-in {
            from { opacity: 0; transform: translateY(${below ? '-6px' : '6px'}); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Pulsing ring on the target itself (does not block clicks).
            Sits in the tour band (< side panel/modals, > device) so any of
            those overlays cover the nudge cleanly. */}
        <div style={{
          position: 'fixed',
          top: targetCy - targetRect.height / 2 - 6,
          left: targetCx - targetRect.width / 2 - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
          borderRadius: 16,
          border: '2px solid rgba(68,194,253,0.9)',
          boxShadow: '0 0 0 0 rgba(68,194,253,0.55)',
          animation: 'onboarding-nudge-pulse 1.6s ease-out infinite',
          zIndex: TOUR_Z_BASE,
          pointerEvents: 'none',
        }} />

        {/* Bouncing pointer (finger/arrow) */}
        <div style={{
          position: 'fixed',
          top: pointerTop,
          left: pointerLeft,
          width: pointerSize,
          height: pointerSize,
          zIndex: TOUR_Z_ACCENT,
          pointerEvents: 'none',
          animation: 'onboarding-nudge-bounce 1s ease-in-out infinite',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))',
        }}>
          <span className="material-icons" style={{
            fontSize: pointerSize,
            color: '#44C2FD',
            transform: below ? 'rotate(180deg)' : 'none',
            display: 'block',
            lineHeight: 1,
          }}>
            arrow_drop_up
          </span>
        </div>

        {/* Caption - also clickable to dismiss/advance the nudge */}
        <div
          onClick={advance}
          role="button"
          tabIndex={0}
          style={{
            position: 'fixed',
            top: clamped.top,
            left: clamped.left,
            width: captionWidth,
            zIndex: TOUR_Z_TOOLTIP,
            background: 'var(--cara-surface)',
            color: 'var(--cara-text)',
            borderRadius: 12,
            padding: '10px 28px 10px 14px', // extra right padding so text clears the X
            fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: 13,
            lineHeight: 1.4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(68,194,253,0.35)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            textAlign: 'center',
            cursor: 'pointer',
            animation: 'onboarding-nudge-fade-in 0.3s ease-out',
          }}
        >
          {/* Dismiss (X) — ends the whole tour, distinct from tapping the
              caption body which advances to the next step. */}
          <button
            onClick={(e) => { e.stopPropagation(); onComplete(); }}
            aria-label="Dismiss"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              borderRadius: 6,
              background: 'transparent',
              color: 'var(--cara-text-muted)',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            <span className="material-icons" style={{ fontSize: 16 }}>close</span>
          </button>
          {step.content}
        </div>
      </>
    );
  }

  // ─────────────────────────── Modal mode ───────────────────────────
  const spotlight = {
    top: targetRect.top - pad,
    left: targetRect.left - pad,
    width: targetRect.width + pad * 2,
    height: targetRect.height + pad * 2,
  };

  const tooltipWidth = 360;
  const tooltipMargin = 16;
  const offset = 16;

  // Anchor the tooltip to the device bezel (right-hand side) when present so
  // the tooltip never overlaps the phone. Falls back to spotlight-relative
  // positioning if the bezel isn't on screen.
  const deviceEl = typeof document !== 'undefined'
    ? document.querySelector('.device-frame-container')
    : null;
  const deviceRect = deviceEl?.getBoundingClientRect();
  const fitsRightOfDevice = deviceRect
    && (window.innerWidth - (deviceRect.right + offset)) >= (tooltipWidth + tooltipMargin);

  // Raw position (transform-free, we clamp the top-left corner directly)
  const tooltipPos = (() => {
    if (fitsRightOfDevice) {
      return { left: deviceRect.right + offset, top: deviceRect.top + deviceRect.height / 2 - 100 };
    }
    switch (placement) {
      case 'right':
        return { left: spotlight.left + spotlight.width + offset, top: spotlight.top };
      case 'left':
        return { left: spotlight.left - tooltipWidth - offset, top: spotlight.top };
      case 'top':
        return { left: spotlight.left + spotlight.width / 2 - tooltipWidth / 2, top: spotlight.top - 280 };
      default: // bottom
        return { left: spotlight.left + spotlight.width / 2 - tooltipWidth / 2, top: spotlight.top + spotlight.height + offset };
    }
  })();

  // Clamp to viewport so the tooltip is never half off-screen.
  // Use a conservative height estimate (260px) for vertical clamping.
  const clampedTooltip = clampToViewport(tooltipPos.left, tooltipPos.top, tooltipWidth, 260, tooltipMargin);

  return (
    <>
      {/* Keyframes for beacon pulse */}
      <style>{`
        @keyframes onboarding-beacon-pulse {
          0%   { transform: scale(1);   opacity: 0.8; }
          50%  { transform: scale(1.6); opacity: 0.2; }
          100% { transform: scale(1);   opacity: 0.8; }
        }
      `}</style>
      {/* Spotlight: transparent box with a huge box-shadow that greys out everything around it */}
      <div style={{
        position: 'fixed',
        top: spotlight.top, left: spotlight.left,
        width: spotlight.width, height: spotlight.height,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
        borderRadius: 4,
        zIndex: TOUR_Z_BASE,
        pointerEvents: 'none',
      }} />
      {/* Pulsing beacon at the centre of the spotlight */}
      <div style={{
        position: 'fixed',
        top: spotlight.top + spotlight.height / 2,
        left: spotlight.left + spotlight.width / 2,
        width: 20, height: 20,
        marginTop: -10, marginLeft: -10,
        borderRadius: '50%',
        background: '#ffffff',
        boxShadow: '0 0 0 2px rgba(255,255,255,0.6), 0 0 18px 4px rgba(255,255,255,0.55)',
        animation: 'onboarding-beacon-pulse 1.4s ease-in-out infinite',
        zIndex: TOUR_Z_ACCENT,
        pointerEvents: 'none',
      }} />
      {/* Tooltip */}
      <div style={{
        position: 'fixed',
        top: clampedTooltip.top,
        left: clampedTooltip.left,
        zIndex: TOUR_Z_TOOLTIP,
        width: tooltipWidth,
        maxWidth: 'calc(100vw - 32px)',
        background: 'var(--cara-surface)',
        color: 'var(--cara-text)',
        borderRadius: 16,
        boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(68,194,253,0.3)',
        padding: 24,
        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
        filter: 'drop-shadow(0 10px 40px rgba(0,0,0,0.5))',
      }}>
        {/* Dismiss (X) — ends the tour from any step. */}
        <button
          onClick={onComplete}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            borderRadius: 8,
            background: 'transparent',
            color: '#6b7280',
            cursor: 'pointer',
            lineHeight: 1,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.background = 'transparent'; }}
        >
          <span className="material-icons" style={{ fontSize: 20 }}>close</span>
        </button>
        {step.title && (
          <h3 style={{ fontSize: 18, fontWeight: 'bold', margin: '0 0 8px', color: 'var(--cara-text)' }}>
            {step.title}
          </h3>
        )}
        <p style={{ margin: 0, lineHeight: 1.5, color: 'var(--cara-text)', fontSize: 14 }}>
          {step.content}
        </p>
        {steps.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '16px 0 0' }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: i === index ? 24 : 8, height: 8, borderRadius: 4,
                background: i === index ? '#44C2FD' : i < index ? 'rgba(68,194,253,0.5)' : '#374151',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          {steps.length > 1 && index > 0 && (
            <button onClick={handleBack} style={{
              padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
              border: '2px solid #44C2FD', background: 'transparent', color: '#44C2FD', cursor: 'pointer',
            }}>Back</button>
          )}
          <button onClick={advance} style={{
            padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600,
            border: 'none', background: 'linear-gradient(135deg, #44C2FD 0%, #23A6D5 100%)',
            color: 'white', cursor: 'pointer', boxShadow: '0 4px 15px rgba(68,194,253,0.4)',
          }}>
            {index === steps.length - 1 ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
};

// Joyride styles
const joyrideStyles = {
  options: {
    arrowColor: 'var(--cara-surface)',
    backgroundColor: 'var(--cara-surface)',
    overlayColor: 'rgba(0, 0, 0, 0.8)',
    primaryColor: '#44C2FD',
    textColor: 'var(--cara-text)',
    spotlightShadow: '0 0 30px rgba(68, 194, 253, 0.6)',
    // Sit in the tour band: above the device (z-index:10) but below the side
    // panel (z-index:50) and all modals (z-index:9999+). floaterProps below
    // pins the tooltip to the same band so react-floater's default (100) can't
    // leak it above modals.
    zIndex: TOUR_Z_BASE,
  },
  // Note: Don't use spotlight.borderRadius - it causes React warnings on SVG elements
  overlay: {
    mixBlendMode: 'normal',
  },
  beacon: {
    inner: '#ffffff',
    outer: '#ffffff',
  },
  beaconInner: {
    backgroundColor: '#ffffff',
  },
  beaconOuter: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    border: '2px solid #ffffff',
  },
};

/**
 * GuidedTour - Dynamic guided tour component that adapts to UI changes
 */
export default function GuidedTour({ 
  autoStart = false,
  onTourComplete, 
  onTourSkip 
}) {
  const dispatch = useDispatch();
  
  // Redux state
  const sidePanelExpanded = useSelector((state) => state.sidePanel.sidePanelExpanded);
  const deviceType = useSelector((state) => state.device.type);
  const currentChannel = useSelector((state) => state.channel.currentChannel);
  const isSettingsModalOpen = useSelector((state) => state.channel.isSettingsModalOpen);
  const isBrandSwitcherOpen = useSelector((state) => state.channel.isBrandSwitcherOpen);
  const isHelpModalOpen = useSelector((state) => state.channel.isHelpModalOpen);
  const isAudioPopoverOpen = useSelector((state) => state.audio.isAudioPopoverOpen);
  // Config-provided onboarding events. A config that defines its own onboarding
  // (video, call, or custom tour steps) takes precedence over the built-in
  // default tour — we must not auto-fire the default tour on top of it.
  const configOnboardingEvents = useSelector((state) => state.channel.onboardingEvents);
  const configId = useSelector((state) => state.channel.configId);

  // Get live modal state to coordinate tour timing
  const { isLiveModalOpen, isOnboardingMode } = useSelector(state => state.live);

  // Onboarding tour steps from config (type: 'tour' onboarding events)
  const onboardingTourSteps = useSelector(state => state.channel.onboardingTourSteps);
  // True between the moment a default-tour onboarding event is queued and the
  // moment we've fired window.startGuidedTour(). Used to delay the nudge
  // overlay so it never appears on top of Joyride.
  const onboardingDefaultTourPending = useSelector(state => state.channel.onboardingDefaultTourPending);

  // Local state — main guided tour
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [joyrideKey, setJoyrideKey] = useState(0); // Key to force Joyride re-render

  // Local state — onboarding tour
  // (runOnboarding state removed — nudge is now derived purely from Redux state)
  
  // Refs
  const updateTimeoutRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Update steps when UI state changes
  const updateSteps = useCallback(() => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    
    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Debounce the update to allow animations to complete
    updateTimeoutRef.current = setTimeout(() => {
      const newSteps = getTourSteps(deviceType, sidePanelExpanded);
      
      // Filter to only steps with visible targets
      const visibleSteps = newSteps.filter(step => {
        try {
          const element = document.querySelector(step.target);
          if (!element) {
            console.log(`🔍 Tour step "${step.id}" target not found: ${step.target}`);
            return false;
          }
          
          const rect = element.getBoundingClientRect();
          const style = window.getComputedStyle(element);
          
          // Check if element is visible and has dimensions
          const isVisible = (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            rect.width > 0 &&
            rect.height > 0
          );
          
          if (!isVisible) {
            console.log(`🔍 Tour step "${step.id}" target hidden: ${step.target}`);
          }
          
          return isVisible;
        } catch (e) {
          console.warn(`🔍 Tour step "${step.id}" selector error:`, e);
          return false;
        }
      });
      
      console.log(`🎯 Tour steps updated: ${visibleSteps.length}/${newSteps.length} visible`);
      
      // Log which steps were filtered out
      newSteps.forEach((step, index) => {
        const isIncluded = visibleSteps.includes(step);
        console.log(`  Step ${index + 1} (${step.id}): ${isIncluded ? '✅ included' : '❌ filtered out'} - target: ${step.target}`);
      });
      
      setSteps(visibleSteps);
      
      // Only force Joyride to recalculate when NOT actively running
      // During active tour, we don't want to reset the key as it remounts the component
      // and loses the current step position
      
      isUpdatingRef.current = false;
    }, 350); // Wait for CSS transitions (typically 300ms)
    
  }, [deviceType, sidePanelExpanded]); // Removed 'run' dependency as it's not used in the callback

  // Initial steps setup and update on state changes
  useEffect(() => {
    updateSteps();
  }, [updateSteps, deviceType, sidePanelExpanded, currentChannel]);

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      if (run) {
        updateSteps();
      }
    };
    
    const handleSkipTour = () => {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      // Release any onboarding nudge that was waiting on this tour to end.
      // (The custom Skip button bypasses Joyride's callback, so we must clear it here.)
      // Always dispatch (idempotent) — guarding on the value here can hit a stale
      // closure capture and leave the flag permanently true.
      dispatch(setOnboardingDefaultTourPending(false));
      console.log('⏭️ Guided tour skipped by user');
      onTourSkip?.();
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('joyride:skip', handleSkipTour);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('joyride:skip', handleSkipTour);
    };
  }, [run, updateSteps, onTourSkip, dispatch, onboardingDefaultTourPending]);

  // Auto-start logic
  // Tour auto-starts on first visit if:
  // 1. autoStart prop is true
  // 2. Tour hasn't been shown before (TOUR_SHOWN_KEY not set in localStorage)
  // 3. Tour hasn't been explicitly disabled (guidedTourEnabled === 'false')
  // 4. NOT currently in onboarding mode or LiveModal is not open
  // 5. Settings modal is not open
  useEffect(() => {
    const tourShown = localStorage.getItem(TOUR_SHOWN_KEY);
    const tourEnabled = localStorage.getItem(TOUR_ENABLED_KEY);
    
    // Don't auto-start if tour was already shown
    if (tourShown === 'true') {
      console.log('🎯 Tour already shown before, skipping auto-start');
      return;
    }
    
    // Don't auto-start if explicitly disabled
    if (tourEnabled === 'false') {
      console.log('🎯 Tour disabled, skipping auto-start');
      return;
    }
    
    // Don't auto-start if settings modal is open
    if (isSettingsModalOpen) {
      console.log('🎯 Settings modal open, delaying tour auto-start');
      return;
    }
    
    // Don't auto-start if we're in onboarding mode or LiveModal is open
    if (isOnboardingMode || isLiveModalOpen) {
      console.log('🎯 Onboarding active or LiveModal open, delaying tour auto-start');
      return;
    }

    // Wait for the config to actually load before deciding. Without this, the
    // 2-second timer can fire during the initial network fetch — before we
    // know whether the config defines its own onboarding flow — and the
    // default tour pops up on top of the brand picker.
    if (!configId) {
      console.log('🎯 Config not yet loaded, delaying tour auto-start');
      return;
    }

    // If the loaded config defines its own onboarding (video / call / custom
    // tour nudge), never auto-fire the built-in default tour — the config
    // author is driving the experience.
    if (Array.isArray(configOnboardingEvents) && configOnboardingEvents.length > 0) {
      console.log('🎯 Config has its own onboardingEvents, skipping default tour auto-start');
      return;
    }

    // Auto-start on first visit after onboarding is complete and settings modal is closed
    if (autoStart) {
      console.log('🎯 First visit detected and all modals closed, auto-starting tour...');
      // Mark tour as shown immediately
      localStorage.setItem(TOUR_SHOWN_KEY, 'true');
      
      const timer = setTimeout(() => {
        updateSteps();
        setRun(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [autoStart, updateSteps, isOnboardingMode, isLiveModalOpen, isSettingsModalOpen, configId, configOnboardingEvents]);

  // Expose global methods
  useEffect(() => {
    window.startGuidedTour = () => {
      console.log('🎯 Starting guided tour...');
      // Stop any current tour first
      setRun(false);
      // Nudge is now gated directly on `run` and Redux state, so once setRun(true)
      // fires below, the render condition flips off automatically — no need to
      // manage a separate runOnboarding flag here.
      // Mark the tour as shown so any 'once'-flagged onboarding default-tour
      // events skip themselves on subsequent loads.
      localStorage.setItem(TOUR_SHOWN_KEY, 'true');

      // Reset state and force Joyride remount
      setStepIndex(0);
      setJoyrideKey(prev => prev + 1);
      
      // Update steps and start after a brief delay
      setTimeout(() => {
        updateSteps();
        setTimeout(() => {
          setRun(true);
          console.log('🚀 Tour started!');
        }, 150);
      }, 100);
    };
    
    window.resetGuidedTour = () => {
      localStorage.removeItem(TOUR_COMPLETED_KEY);
      localStorage.removeItem(TOUR_SHOWN_KEY); // Also remove shown flag
      setRun(false);
      setStepIndex(0);
      setJoyrideKey(prev => prev + 1);
      console.log('🔄 Tour reset');
    };
    
    window.enableGuidedTour = () => {
      localStorage.setItem(TOUR_ENABLED_KEY, 'true');
      localStorage.removeItem(TOUR_COMPLETED_KEY);
      localStorage.removeItem(TOUR_SHOWN_KEY); // Allow tour to auto-start again
      console.log('✅ Guided tour enabled - call window.startGuidedTour() to begin');
    };
    
    window.disableGuidedTour = () => {
      localStorage.setItem(TOUR_ENABLED_KEY, 'false');
      setRun(false);
      console.log('❌ Guided tour disabled');
    };
    
    window.tourStatus = () => {
      console.log('📊 Tour Status:', {
        enabled: localStorage.getItem(TOUR_ENABLED_KEY) === 'true',
        completed: !!localStorage.getItem(TOUR_COMPLETED_KEY),
        shown: !!localStorage.getItem(TOUR_SHOWN_KEY),
        isRunning: run,
        currentStep: stepIndex + 1,
        totalSteps: steps.length,
        deviceType,
        sidePanelExpanded,
      });
    };

    // Debug: Go to specific step
    window.tourGoToStep = (index) => {
      if (index >= 0 && index < steps.length) {
        setStepIndex(index);
        setJoyrideKey(prev => prev + 1);
        console.log(`📍 Jumped to step ${index + 1}`);
      }
    };

    return () => {
      delete window.startGuidedTour;
      delete window.resetGuidedTour;
      delete window.enableGuidedTour;
      delete window.disableGuidedTour;
      delete window.tourStatus;
      delete window.tourGoToStep;
    };
  }, [run, stepIndex, steps.length, deviceType, sidePanelExpanded, updateSteps]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Handle Joyride callbacks
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type, step } = data;
    
    console.log('🎭 Tour callback:', { action, index, status, type, stepId: step?.id });

    // Tour completed or skipped
    if (status === STATUS.FINISHED) {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      // Release any onboarding nudge that was waiting on this tour to end.
      // Always dispatch (idempotent) — don't gate on a value that could be stale
      // due to useCallback closure.
      dispatch(setOnboardingDefaultTourPending(false));
      console.log('✅ Guided tour completed!');
      onTourComplete?.();
      return;
    }

    if (status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      dispatch(setOnboardingDefaultTourPending(false));
      console.log('⏭️ Guided tour skipped');
      onTourSkip?.();
      return;
    }

    // Handle close button - mark as completed so it doesn't restart on refresh
    if (action === ACTIONS.CLOSE) {
      setRun(false);
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      dispatch(setOnboardingDefaultTourPending(false));
      console.log('❌ Tour closed by user');
      return;
    }

    // Auto-expand side panel when approaching that step
    if (type === EVENTS.STEP_BEFORE) {
      if (step?.id === STEP_IDS.SIDEPANEL && !sidePanelExpanded) {
        console.log('📂 Expanding side panel for tour step...');
        dispatch(toggleSidePanel());
      }
      
      // Navigate to home screen before showing the "Home Screen" step
      if (step?.id === STEP_IDS.CHANNELS) {
        console.log('🏠 Navigating to home screen for tour step...');
        if (currentChannel !== 'home') {
          dispatch(setChannel('home'));
        }
      }
      
      // Navigate to home for the "Get Started" step so user can see the Phone app
      if (step?.id === STEP_IDS.GET_STARTED) {
        console.log('🏠 Navigating to home for Get Started step...');
        dispatch(setChannel('home'));
      }
    }

    // After leaving the Home Button step, simulate a home button press
    if (type === EVENTS.STEP_AFTER && step?.id === STEP_IDS.HOME_BUTTON) {
      console.log('🏠 Home Button step complete — navigating to home screen...');
      if (currentChannel !== 'home') {
        dispatch(setChannel('home'));
      }
    }

    // Handle target not found - Joyride will auto-skip
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn('⚠️ Target not found for step:', step?.target);
    }
  }, [sidePanelExpanded, dispatch, onTourComplete, onTourSkip, currentChannel]);

  // Release the nudge-gate whenever the default tour transitions from
  // running to not-running. Belt-and-braces in case any Joyride end path
  // (Finish/Skip/Close/internal stop) doesn't hit our explicit callback
  // branches. We use a ref to ignore the very first false→false transition
  // on mount (before the tour has ever started).
  const tourHasBeenRunningRef = useRef(false);
  useEffect(() => {
    if (run) {
      tourHasBeenRunningRef.current = true;
    } else if (tourHasBeenRunningRef.current) {
      tourHasBeenRunningRef.current = false;
      dispatch(setOnboardingDefaultTourPending(false));
    }
  }, [run, dispatch]);

  // The nudge renders directly from Redux state — no buffered `runOnboarding`
  // state or 1-second timer to get out of sync. Gating conditions:
  //  - we have nudge steps queued
  //  - the LiveModal is closed
  //  - no blocking modal is open (Settings/Brand/Help). These render in
  //    framer-motion stacking contexts whose effective root-level paint can sit
  //    below the fixed nudge, so a z-index alone won't keep the nudge under
  //    them; gating the nudge off while any is open is the reliable fix (mirrors
  //    the LiveModal gate). The flags are synced from App's local state.
  //  - the default tour isn't queued (pending) or running
  const shouldShowNudge = (
    Array.isArray(onboardingTourSteps)
    && onboardingTourSteps.length > 0
    && !isLiveModalOpen
    && !isSettingsModalOpen
    && !isBrandSwitcherOpen
    && !isHelpModalOpen
    && !isAudioPopoverOpen
    && !onboardingDefaultTourPending
    && !run
  );

  const handleOnboardingTourDone = useCallback(() => {
    dispatch(setOnboardingTourSteps(null));
  }, [dispatch]);

  // Don't render if neither tour has anything to show
  if (steps.length === 0 && !shouldShowNudge) return null;

  return (
    <>
    {shouldShowNudge && (
      <OnboardingTourOverlay
        key={JSON.stringify(onboardingTourSteps)}
        steps={onboardingTourSteps}
        onComplete={handleOnboardingTourDone}
      />
    )}
    {steps.length > 0 && <Joyride
      key={joyrideKey}
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      hideCloseButton={false}
      disableOverlayClose
      disableScrolling={false}
      disableScrollParentFix={false}
      scrollToFirstStep={false}
      spotlightClicks={false}
      styles={joyrideStyles}
      tooltipComponent={CustomTooltip}
      callback={handleJoyrideCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      floaterProps={{
        disableAnimation: false,
        hideArrow: false,
        offset: 15,
        // Pin the tooltip into the tour z-band so react-floater's default
        // (100) can't leak it above modals.
        options: { zIndex: TOUR_Z_TOOLTIP },
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 40px rgba(0, 0, 0, 0.5))',
          },
        },
      }}
    />}
    </>
  );
}

// Export helper functions
export const startTour = () => window.startGuidedTour?.();
export const resetTour = () => window.resetGuidedTour?.();
export const enableTour = () => window.enableGuidedTour?.();
export const disableTour = () => window.disableGuidedTour?.();
export const getTourStatus = () => window.tourStatus?.();
